import { createContext, createElement, useContext, useMemo } from "react";
import type { ReactNode } from "react";

import type { ICitationResolution } from "@/api/chat/chat.types";

// Handle -> resolution lookup for inline `cite://cN` chips. The model never
// authors a page or document id; the backend stamps each handle and ships a
// resolution map (live via the `citation_resolved` stream event, on reload via
// the persisted message). Building a Map keyed by handle gives O(1) lookup and
// makes duplicate handles last-registration-wins for free.
const CitationResolutionContext = createContext<
    Map<string, ICitationResolution>
>(new Map());

export function CitationResolutionProvider({
    resolutions,
    children,
}: {
    resolutions: ICitationResolution[] | undefined;
    children: ReactNode;
}) {
    const map = useMemo(() => {
        const m = new Map<string, ICitationResolution>();
        for (const r of resolutions ?? []) m.set(r.handle, r);
        return m;
    }, [resolutions]);
    return createElement(
        CitationResolutionContext.Provider,
        { value: map },
        children
    );
}

export const useCitationResolution = (
    handle: string
): ICitationResolution | undefined =>
    useContext(CitationResolutionContext).get(handle);
